// client/src/components/ClientPicker.jsx
//
// Autocomplete client selector with inline "Add new client" flow. Replaces the
// raw text inputs for client_name/client_email in places where we want to link
// to the clients table.
//
// Three possible states a presentation/PO can end up in:
//   1. Linked to a client (clientId set). Picker shows the client's name as a
//      pill. "Change" removes the pill and reopens the picker.
//   2. Free-text only (typed something but never picked or added). Saved as
//      client_name/client_email on the row with no client_id. Legacy mode —
//      useful for one-off quotes.
//   3. Blank (state 3 = no client at all). Legitimate for internal drafts.
//
// Behavior:
//   - Type ≥2 chars → autocomplete dropdown queries /api/clients?q=
//   - Dropdown shows up to 6 matches, each with name + email below
//   - Last row always: "+ Add new client: '{typed text}'" → opens AddClientModal
//   - Click a match → calls onPick({ id, name, email }), picker closes
//   - Saving the AddClientModal → POSTs to /api/clients, then onPick with the new client
//   - "X" on the linked-client pill → calls onPick(null) (clears link)
//
// Props:
//   value:    { id, name, email } | null (current linked client, if any)
//   getToken: Clerk token getter
//   onPick:   (client | null) => void
//   onClear:  () => void  (called when user explicitly clears all client info,
//                          including any free-text fallback held by the parent)
//   fallbackName / fallbackEmail: for displaying state 2 (free-text only, no
//             clientId yet). The picker shows these as the input's current
//             value if no clientId is set. Lets users edit existing rows that
//             were saved before clients existed.
//   onChangeFreetext: ({ name?, email? }) => void
//             Called when user types in the name/email inputs without picking
//             a client from autocomplete. Lets the parent persist state 2.

import { useState, useEffect, useRef } from 'react';
import { api } from '../api';
import { BRAND } from '../config/brand';

const G = BRAND.colors.primary;

export default function ClientPicker({
  value, fallbackName = '', fallbackEmail = '',
  getToken, onPick, onClear, onChangeFreetext,
}) {
  const [showAddModal, setShowAddModal] = useState(false);
  const [addPrefillName, setAddPrefillName] = useState('');

  if (value && value.id) {
    // ── State 1: linked client ─────────────────────────────────────────────
    return (
      <div style={{ display:'flex',alignItems:'center',gap:8,padding:'10px 12px',
        background:'#F0FDF4',border:`1px solid ${G}33`,borderRadius:8 }}>
        <div style={{
          width:30,height:30,borderRadius:'50%',background:G,color:'#fff',
          display:'flex',alignItems:'center',justifyContent:'center',
          fontSize:13,fontWeight:600,flexShrink:0,
        }}>
          {(value.name || '?')[0].toUpperCase()}
        </div>
        <div style={{ flex:1,minWidth:0 }}>
          <div style={{ fontSize:13,fontWeight:600,color:'#0F172A',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis' }}>
            {value.name}
          </div>
          {value.email && (
            <div style={{ fontSize:11,color:'#64748B',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis' }}>
              {value.email}
            </div>
          )}
        </div>
        <button onClick={() => { onPick(null); onClear?.(); }}
          aria-label="Remove client"
          style={{ background:'none',border:'1px solid #E2E8F0',borderRadius:6,padding:'4px 9px',
            fontSize:11,color:'#475569',cursor:'pointer',fontFamily:'inherit' }}>
          Change
        </button>
      </div>
    );
  }

  // ── States 2 + 3: no linked client yet ───────────────────────────────────
  return (
    <>
      <PickerInputs
        fallbackName={fallbackName} fallbackEmail={fallbackEmail}
        getToken={getToken}
        onPick={onPick}
        onChangeFreetext={onChangeFreetext}
        onOpenAdd={(prefill) => { setAddPrefillName(prefill); setShowAddModal(true); }}
      />
      {showAddModal && (
        <AddClientModal
          prefillName={addPrefillName}
          prefillEmail={fallbackEmail}
          getToken={getToken}
          onClose={() => setShowAddModal(false)}
          onCreated={(newClient) => {
            setShowAddModal(false);
            onPick({ id: newClient.id, name: newClient.name, email: newClient.email });
          }}
        />
      )}
    </>
  );
}

function PickerInputs({ fallbackName, fallbackEmail, getToken, onPick, onChangeFreetext, onOpenAdd }) {
  const [nameInput,  setNameInput]  = useState(fallbackName);
  const [emailInput, setEmailInput] = useState(fallbackEmail);
  const [matches, setMatches] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  // Keep inputs in sync if parent's fallback values change (e.g., loading an
  // existing presentation). We deliberately don't override after the user
  // starts typing, since that would clobber their input.
  useEffect(() => { setNameInput(fallbackName); }, [fallbackName]);
  useEffect(() => { setEmailInput(fallbackEmail); }, [fallbackEmail]);

  // Debounced autocomplete fetch. Only triggers at 2+ chars to avoid noisy
  // 1-char queries that match too much.
  useEffect(() => {
    clearTimeout(debounceRef.current);
    const q = nameInput.trim();
    if (q.length < 2) { setMatches([]); return; }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const token = await getToken();
        const list = await api.withToken(token).listClients(q);
        setMatches(list);
      } catch { setMatches([]); }
      finally { setLoading(false); }
    }, 200);
    return () => clearTimeout(debounceRef.current);
  }, [nameInput, getToken]);

  // Close dropdown when clicking outside.
  useEffect(() => {
    const onDoc = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const dropdownItems = open ? [
    ...matches.slice(0, 6).map(c => ({ type:'client', data:c })),
    ...(nameInput.trim().length >= 2
        ? [{ type:'add', data:{ name: nameInput.trim() } }]
        : []),
  ] : [];

  const pick = (it) => {
    if (it.type === 'client') {
      onPick({ id: it.data.id, name: it.data.name, email: it.data.email });
      setOpen(false);
    } else if (it.type === 'add') {
      setOpen(false);
      onOpenAdd(it.data.name);
    }
  };

  const onKey = (e) => {
    if (!open || dropdownItems.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx(i => Math.min(i + 1, dropdownItems.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && activeIdx >= 0) {
      e.preventDefault();
      pick(dropdownItems[activeIdx]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  // Two inputs side-by-side, like the original presentation form. Name is the
  // autocomplete trigger; email is plain free-text. Both update parent state
  // via onChangeFreetext so the row can be saved without picking a client.
  return (
    <div ref={containerRef} style={{ position:'relative' }}>
      <div style={{ display:'flex',gap:8 }}>
        <input value={nameInput}
          onChange={e => { setNameInput(e.target.value); setOpen(true); setActiveIdx(-1); onChangeFreetext?.({ name: e.target.value }); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKey}
          placeholder="Client name"
          style={{ flex:1,border:'1px solid #E2E8F0',borderRadius:8,padding:'10px 12px',fontSize:13,fontFamily:'inherit',outline:'none',color:'#0F172A',background:'#fff' }}/>
        <input value={emailInput}
          onChange={e => { setEmailInput(e.target.value); onChangeFreetext?.({ email: e.target.value }); }}
          placeholder="Client email (optional)"
          type="email"
          style={{ flex:1,border:'1px solid #E2E8F0',borderRadius:8,padding:'10px 12px',fontSize:13,fontFamily:'inherit',outline:'none',color:'#0F172A',background:'#fff' }}/>
      </div>

      {open && dropdownItems.length > 0 && (
        <div style={{ position:'absolute',top:'100%',left:0,right:0,marginTop:4,
          background:'#fff',border:'1px solid #E2E8F0',borderRadius:8,
          boxShadow:'0 8px 24px rgba(15,23,42,0.08)',zIndex:50,maxHeight:280,overflowY:'auto' }}>
          {loading && <div style={{ padding:'8px 12px',fontSize:11,color:'#94A3B8' }}>Searching…</div>}
          {dropdownItems.map((it, i) => {
            const isActive = i === activeIdx;
            const baseStyle = {
              padding:'10px 12px',cursor:'pointer',
              background: isActive ? '#F8FAFC' : '#fff',
              borderBottom: i < dropdownItems.length - 1 ? '1px solid #F1F5F9' : 'none',
            };
            if (it.type === 'client') {
              return (
                <div key={`c-${it.data.id}`} onClick={() => pick(it)} onMouseEnter={() => setActiveIdx(i)} style={baseStyle}>
                  <div style={{ fontSize:13,fontWeight:500,color:'#0F172A' }}>{it.data.name}</div>
                  {it.data.email && (
                    <div style={{ fontSize:11,color:'#64748B',marginTop:2 }}>{it.data.email}</div>
                  )}
                </div>
              );
            }
            return (
              <div key="add" onClick={() => pick(it)} onMouseEnter={() => setActiveIdx(i)}
                style={{ ...baseStyle, color: G, fontSize:13, fontWeight:500 }}>
                + Add new client: "<span style={{ color:'#0F172A' }}>{it.data.name}</span>"
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function AddClientModal({ prefillName, prefillEmail, getToken, onClose, onCreated }) {
  const [companyName, setCompanyName] = useState(prefillName || '');
  const [contactName, setContactName] = useState('');
  const [email, setEmail] = useState(prefillEmail || '');
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [showMore, setShowMore] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  // Display/primary name is the company if given, else the contact.
  const displayName = (companyName.trim() || contactName.trim());

  const save = async () => {
    if (!displayName) { setErr('Enter a company or contact name'); return; }
    setSaving(true); setErr(null);
    try {
      const token = await getToken();
      const created = await api.withToken(token).createClient({
        name: displayName,
        company_name: companyName.trim() || null,
        contact_name: contactName.trim() || null,
        email: email.trim() || null,
        phone: phone.trim() || null,
        notes: notes.trim() || null,
      });
      onCreated(created);
    } catch (e) {
      setErr(e.message || 'Could not save');
      setSaving(false);
    }
  };

  return (
    <div style={{ position:'fixed',inset:0,background:'rgba(15,23,42,0.55)',zIndex:200,
      display:'flex',alignItems:'center',justifyContent:'center',backdropFilter:'blur(4px)' }}
      onClick={e => e.target === e.currentTarget && !saving && onClose()}>
      <div style={{ background:'#fff',borderRadius:14,padding:28,maxWidth:420,width:'90%',
        boxShadow:'0 20px 60px rgba(0,0,0,0.25)' }}>
        <div style={{ fontSize:18,fontWeight:600,color:'#0F172A',marginBottom:6 }}>Add a new client</div>
        <div style={{ fontSize:12,color:'#64748B',marginBottom:18,lineHeight:1.5 }}>
          A company or contact name to get going — add the rest now or edit later in the Clients page.
        </div>
        <div style={{ marginBottom:12 }}>
          <label style={{ display:'block',fontSize:11,fontWeight:500,color:'#374151',marginBottom:4 }}>Company name</label>
          <input value={companyName} onChange={e => setCompanyName(e.target.value)} autoFocus
            placeholder="Collector's Summit"
            style={{ width:'100%',border:'1px solid #E2E8F0',borderRadius:7,padding:'9px 11px',fontSize:13,fontFamily:'inherit',outline:'none',color:'#0F172A',background:'#fff',boxSizing:'border-box' }}/>
        </div>
        <div style={{ marginBottom:12 }}>
          <label style={{ display:'block',fontSize:11,fontWeight:500,color:'#374151',marginBottom:4 }}>Contact name</label>
          <input value={contactName} onChange={e => setContactName(e.target.value)}
            placeholder="Jane Smith"
            style={{ width:'100%',border:'1px solid #E2E8F0',borderRadius:7,padding:'9px 11px',fontSize:13,fontFamily:'inherit',outline:'none',color:'#0F172A',background:'#fff',boxSizing:'border-box' }}/>
        </div>
        <div style={{ marginBottom: showMore ? 12 : 16 }}>
          <label style={{ display:'block',fontSize:11,fontWeight:500,color:'#374151',marginBottom:4 }}>Email</label>
          <input value={email} onChange={e => setEmail(e.target.value)} type="email"
            placeholder="admin@collectorssummit.com"
            style={{ width:'100%',border:'1px solid #E2E8F0',borderRadius:7,padding:'9px 11px',fontSize:13,fontFamily:'inherit',outline:'none',color:'#0F172A',background:'#fff',boxSizing:'border-box' }}/>
        </div>

        {!showMore ? (
          <button type="button" onClick={() => setShowMore(true)}
            style={{ background:'none',border:'none',color:G,cursor:'pointer',fontSize:12,fontWeight:500,padding:'0 0 16px',fontFamily:'inherit' }}>
            + Add phone &amp; notes
          </button>
        ) : (
          <>
            <div style={{ marginBottom:12 }}>
              <label style={{ display:'block',fontSize:11,fontWeight:500,color:'#374151',marginBottom:4 }}>Phone</label>
              <input value={phone} onChange={e => setPhone(e.target.value)}
                placeholder="(206) 555-0123"
                style={{ width:'100%',border:'1px solid #E2E8F0',borderRadius:7,padding:'9px 11px',fontSize:13,fontFamily:'inherit',outline:'none',color:'#0F172A',background:'#fff',boxSizing:'border-box' }}/>
            </div>
            <div style={{ marginBottom:16 }}>
              <label style={{ display:'block',fontSize:11,fontWeight:500,color:'#374151',marginBottom:4 }}>Notes</label>
              <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3}
                placeholder="Anything useful about this client…"
                style={{ width:'100%',border:'1px solid #E2E8F0',borderRadius:7,padding:'9px 11px',fontSize:13,fontFamily:'inherit',outline:'none',color:'#0F172A',background:'#fff',boxSizing:'border-box',resize:'vertical' }}/>
            </div>
          </>
        )}
        {err && (
          <div style={{ marginBottom:12,padding:'8px 10px',background:'#FEF2F2',border:'1px solid #FECACA',borderRadius:7,fontSize:11,color:'#991B1B' }}>{err}</div>
        )}
        <div style={{ display:'flex',gap:10,justifyContent:'flex-end' }}>
          <button onClick={onClose} disabled={saving}
            style={{ background:'#fff',border:'1px solid #E2E8F0',borderRadius:7,padding:'9px 16px',fontSize:12,color:'#475569',cursor:'pointer',fontFamily:'inherit' }}>
            Cancel
          </button>
          <button onClick={save} disabled={saving || !displayName}
            style={{ background:!displayName ? '#E2E8F0' : G,border:'none',borderRadius:7,padding:'9px 18px',fontSize:12,color:'#fff',cursor:(saving||!displayName)?'not-allowed':'pointer',fontFamily:'inherit',fontWeight:500 }}>
            {saving ? 'Saving…' : 'Create client'}
          </button>
        </div>
      </div>
    </div>
  );
}
